import { useEffect, useState, useCallback } from 'react';
import { Rows3, Rows4 } from 'lucide-react';

/**
 * Density preference (compact vs comfortable).
 *
 * When `compact` is on, list-heavy pages drop ~30% vertical padding so power
 * users (admins doing approvals, casting directors browsing talent) can
 * scan more rows per screen. The preference is persisted to localStorage
 * and exposed as a `data-density` attribute on `<html>` so any component
 * can opt in via CSS:
 *
 *   html[data-density="compact"] [data-density-aware] { padding-y: smaller; }
 *
 * Components that want to react in JS just call `useDensity()`.
 */
const STORAGE_KEY = 'ccuk_density';

function readInitial() {
  if (typeof window === 'undefined') return 'comfortable';
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'compact' ? 'compact' : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

function applyToDom(density) {
  if (typeof document === 'undefined') return;
  try {
    document.documentElement.setAttribute('data-density', density);
  } catch { /* ignore — iOS Safari in some private-mode configs throws */ }
}

// Apply synchronously at module load so first paint matches the saved pref.
// Wrapped in try/catch so a broken localStorage / document access can't
// crash the whole bundle on iOS Safari.
try { applyToDom(readInitial()); } catch { /* ignore */ }

export function useDensity() {
  const [density, setDensity] = useState(readInitial);

  useEffect(() => {
    applyToDom(density);
    try { localStorage.setItem(STORAGE_KEY, density); } catch { /* ignore quota */ }
  }, [density]);

  const toggle = useCallback(() => {
    setDensity((d) => (d === 'compact' ? 'comfortable' : 'compact'));
  }, []);

  return { density, isCompact: density === 'compact', setDensity, toggle };
}

/**
 * Small inline button you can drop into any toolbar to flip the density.
 */
export function DensityToggleButton({ className = '' }) {
  const { isCompact, toggle } = useDensity();
  return (
    <button
      type="button"
      onClick={toggle}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-theme bg-theme-secondary text-theme-muted hover:text-theme-text hover:border-theme-primary/40 transition-colors ${className}`}
      title={isCompact ? 'Switch to comfortable rows' : 'Switch to compact rows'}
      data-testid="density-toggle"
    >
      {isCompact ? <Rows4 className="h-3.5 w-3.5" /> : <Rows3 className="h-3.5 w-3.5" />}
      <span className="hidden sm:inline">{isCompact ? 'Comfortable' : 'Compact'}</span>
    </button>
  );
}
